import {
  Button,
  ErrorMessage,
  notify,
  useConfirmModal,
} from '@affine/component';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { BlobManagementService } from '@affine/core/modules/blob-management/services';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import type { ListedBlobRecord } from '@affine/nbstore';
import track from '@affine/track';
import { DeleteIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { StorageFileGrid } from './storage-file-grid';

export const BlobManagementPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();

  const unusedBlobsEntity = useService(BlobManagementService).unusedBlobs;
  const originalUnusedBlobs = useLiveData(unusedBlobsEntity.unusedBlobs$);
  const isLoading = useLiveData(unusedBlobsEntity.isLoading$);
  const state = useLiveData(unusedBlobsEntity.state$);
  const error = useLiveData(unusedBlobsEntity.error$);
  const hasMore = useLiveData(unusedBlobsEntity.hasMore$);
  const [unusedBlobs, setUnusedBlobs] = useState<ListedBlobRecord[]>([]);

  useEffect(() => {
    setUnusedBlobs(originalUnusedBlobs);
  }, [originalUnusedBlobs]);

  useEffect(() => {
    if (backendAvailable) {
      void unusedBlobsEntity.revalidate();
    }
  }, [backendAvailable, unusedBlobsEntity]);

  const [selectedBlobs, setSelectedBlobs] = useState<ListedBlobRecord[]>([]);
  const [deleting, setDeleting] = useState(false);

  const handleUnselectBlob = useCallback((blob: ListedBlobRecord) => {
    setSelectedBlobs(prev => prev.filter(b => b.key !== blob.key));
  }, []);

  const { openConfirmModal } = useConfirmModal();

  const handleDeleteSelectedBlobs = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      if (!backendAvailable) {
        notify.warning({
          title: t['com.streamlain.settings.connection-required.title'](),
          message:
            t['com.streamlain.settings.connection-required.description'](),
        });
        return;
      }

      const currentSelectedBlobs = selectedBlobs;
      openConfirmModal({
        title:
          t[
            'com.streamlain.settings.workspace.storage.unused-blobs.delete.title'
          ](),
        children:
          t[
            'com.streamlain.settings.workspace.storage.unused-blobs.delete.warning'
          ](),
        onConfirm: async () => {
          setDeleting(true);
          try {
            track.$.settingsPanel.workspace.deleteUnusedBlob();
            for (const blob of currentSelectedBlobs) {
              await unusedBlobsEntity.deleteBlob(blob.key, true);
              markBackendAvailable();
              handleUnselectBlob(blob);
              setUnusedBlobs(prev => prev.filter(b => b.key !== blob.key));
            }
          } catch (error) {
            if (isBackendUnavailableError(error)) {
              markBackendUnavailable(error);
              notify.warning({
                title: t['com.streamlain.settings.connection-required.title'](),
                message:
                  t[
                    'com.streamlain.settings.connection-required.description'
                  ](),
              });
            } else {
              notify.error({
                title:
                  t[
                    'com.streamlain.settings.workspace.storage.unused-blobs.delete.title'
                  ](),
                message: error instanceof Error ? error.message : String(error),
              });
            }
          } finally {
            setDeleting(false);
          }
        },
        confirmText: t['Delete'](),
        cancelText: t['Cancel'](),
        confirmButtonOptions: {
          variant: 'error',
        },
      });
    },
    [
      backendAvailable,
      selectedBlobs,
      openConfirmModal,
      t,
      unusedBlobsEntity,
      handleUnselectBlob,
    ]
  );

  return (
    <>
      <StorageFileGrid
        backendAvailable={backendAvailable}
        description={t.t(
          'com.streamlain.settings.workspace.storage.unused-blobs.desc'
        )}
        emptyText={
          backendAvailable
            ? t[
                'com.streamlain.settings.workspace.storage.unused-blobs.empty'
              ]()
            : t['com.streamlain.settings.connection-required.description']()
        }
        files={unusedBlobs}
        hideEmpty={false}
        loading={isLoading && backendAvailable}
        renderSelectionActions={() => (
          <Button
            loading={deleting}
            onClick={handleDeleteSelectedBlobs}
            prefix={<DeleteIcon />}
            disabled={deleting || !backendAvailable}
            title={
              !backendAvailable
                ? t['com.streamlain.settings.connection-required.description']()
                : undefined
            }
          >
            {t['Delete']()}
          </Button>
        )}
        selectable
        selectedFiles={selectedBlobs}
        selectedLabel={t[
          'com.streamlain.settings.workspace.storage.unused-blobs.selected'
        ]()}
        selectAllLabel={t['com.streamlain.keyboardShortcuts.selectAll']()}
        title={t['com.streamlain.settings.workspace.storage.unused-blobs']()}
        onSelectedFilesChange={setSelectedBlobs}
      />
      {state === 'error' ? (
        <ErrorMessage>
          {error?.message ?? 'Unable to load unused files.'}{' '}
          <Button onClick={() => void unusedBlobsEntity.revalidate()}>
            Retry
          </Button>
        </ErrorMessage>
      ) : null}
      {state === 'ready' && hasMore ? (
        <Button
          loading={isLoading}
          onClick={() => void unusedBlobsEntity.loadNextPage()}
        >
          Load more
        </Button>
      ) : null}
    </>
  );
};
